import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import {
  WarningBanner,
  FieldLabel,
  useConfirm,
} from "./shared/components";
import {
  getNoDuesDeptInbox,
  getNoDuesDeptProcessed,
  getNoDuesDepartmentsConfig,
  getNoDuesFormDetails,
  getResignationDetails,
  listMySubNodals,
  createSubNodal,
  deleteSubNodal,
  delegateDispatch,
  revokeDelegation,
  getDelegatedByMe,
  getCurrentEmpNo,
  NoDuesDispatchRow,
  NoDuesDepartmentConfig,
  NoDuesSubNodal,
} from "./services/eSeparation.service";

const statusMeta = (s: string) => {
  const u = String(s || "").toUpperCase();
  if (u === "CLEARED") return { label: "Cleared", bg: "#E4F7E8", color: "#049123" };
  if (u === "REJECTED") return { label: "Dues Found", bg: "#FDE5E5", color: "#C0282C" };
  return { label: "Pending", bg: "#FC9D4520", color: "#BA7517" };
};

const padEmpNo = (v?: string | null): string => {
  const t = (v ?? "").trim();
  return t ? t.padStart(8, "0") : "";
};

const NoDuesDeptClearance: React.FC = () => {
  const navigate = useNavigate();
  const routes = all_routes;
  const { confirm, confirmDialog } = useConfirm();

  const [tab, setTab] = useState<"Pending" | "Processed">("Pending");
  const [pending, setPending] = useState<NoDuesDispatchRow[]>([]);
  const [processed, setProcessed] = useState<NoDuesDispatchRow[]>([]);
  const [loading, setLoading] = useState(true);

  // Map to hold employee name, emp_no and designation for each request_id
  const [empInfoMap, setEmpInfoMap] = useState<
    Record<string, { emp_name?: string; emp_no?: string; designation?: string }>
  >({});

  // Sub-Nodal management — only relevant for the departments the caller is the
  // Nodal Officer (approver_emp_id) of.
  const [myDepartments, setMyDepartments] = useState<NoDuesDepartmentConfig[]>([]);
  const [subNodals, setSubNodals] = useState<NoDuesSubNodal[]>([]);
  const [delegated, setDelegated] = useState<NoDuesDispatchRow[]>([]);
  const [newSubNodalDeptId, setNewSubNodalDeptId] = useState<number | "">("");
  const [newSubNodalEmpId, setNewSubNodalEmpId] = useState("");
  const [savingSubNodal, setSavingSubNodal] = useState(false);

  // Delegate modal state.
  const [delegateRow, setDelegateRow] = useState<NoDuesDispatchRow | null>(null);
  const [delegateTarget, setDelegateTarget] = useState("");
  const [delegating, setDelegating] = useState(false);

  const myEmpId = padEmpNo(getCurrentEmpNo());

  const load = async () => {
    setLoading(true);
    const [inbox, done, deptConfig, subs, delegatedByMe] = await Promise.all([
      getNoDuesDeptInbox(),
      getNoDuesDeptProcessed(),
      getNoDuesDepartmentsConfig(),
      listMySubNodals(),
      getDelegatedByMe(),
    ]);
    const inboxRows = inbox.data || [];
    const doneRows = done.data || [];
    setPending(inboxRows);
    setProcessed(doneRows);
    setMyDepartments(
      (deptConfig.data || []).filter(
        (d) => !d.is_settlement && padEmpNo(d.approver_emp_id) === myEmpId
      )
    );
    setSubNodals(subs.data || []);
    setDelegated(delegatedByMe.data || []);
    setLoading(false);

    // Fetch employee details for all unique requests
    const allRows = [...inboxRows, ...doneRows];
    const uniqueReqIds = Array.from(
      new Set(allRows.map((r) => r.request_id).filter(Boolean))
    );

    const infoMap: Record<
      string,
      { emp_name?: string; emp_no?: string; designation?: string }
    > = {};

    await Promise.all(
      uniqueReqIds.map(async (reqId) => {
        try {
          const ndRes = await getNoDuesFormDetails(reqId);
          const ndData = ndRes.data;
          const parentId =
            ndData?.request_id || ndData?.orig_request_id || reqId;

          let empName = ndData?.emp_name || ndData?.details?.emp_name;
          let empNo = ndData?.emp_no || ndData?.details?.emp_no;
          let designation = ndData?.designation || ndData?.details?.designation;

          if (!empName || !empNo) {
            const resRes = await getResignationDetails(parentId);
            if (resRes.data) {
              empName = empName || resRes.data.emp_name;
              empNo = empNo || resRes.data.emp_no;
              designation = designation || resRes.data.designation;
            }
          }
          if (empName || empNo) {
            infoMap[reqId] = { emp_name: empName, emp_no: empNo, designation };
          }
        } catch {
          // silent fallback
        }
      })
    );

    setEmpInfoMap((prev) => ({ ...prev, ...infoMap }));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isNodalOwner = myDepartments.length > 0;

  const openAction = (row: NoDuesDispatchRow) => {
    navigate(`${routes.Esepration.noDuesDeptAction}/${row.id}`, {
      state: {
        dispatch_id: row.id,
        department_id: row.department_id,
        department_name: row.department_name,
        request_id: row.request_id,
      },
    });
  };

  const addSubNodal = async () => {
    const departmentId =
      newSubNodalDeptId === "" ? myDepartments[0]?.id : newSubNodalDeptId;
    if (!departmentId) return toast.warning("Select a department.");
    if (!newSubNodalEmpId.trim())
      return toast.warning("Enter the 8-digit employee ID.");
    setSavingSubNodal(true);
    const res = await createSubNodal({
      department_id: Number(departmentId),
      sub_nodal_emp_id: newSubNodalEmpId.trim(),
    });
    setSavingSubNodal(false);
    if (res.success) {
      toast.success("Sub-Nodal added successfully.");
      setNewSubNodalEmpId("");
      load();
    } else {
      toast.error(res.message || "Failed to add sub-nodal.");
    }
  };

  const removeSubNodal = async (sub: NoDuesSubNodal) => {
    if (
      !(await confirm(
        `Remove ${sub.sub_nodal_name || sub.sub_nodal_emp_id} as sub-nodal for ${sub.department_name}?`,
        { title: "Remove Sub-Nodal", confirmLabel: "Remove", tone: "danger" }
      ))
    )
      return;
    const res = await deleteSubNodal(sub.id);
    if (res.success) {
      toast.success("Sub-Nodal removed.");
      load();
    } else {
      toast.error(res.message || "Failed to remove sub-nodal.");
    }
  };

  // Sub-nodals available for delegating a specific row (matching row's department).
  const subNodalsForRow = (row: NoDuesDispatchRow): NoDuesSubNodal[] =>
    subNodals.filter(
      (s) => s.department_id === row.department_id && s.is_active
    );

  const openDelegate = (row: NoDuesDispatchRow) => {
    setDelegateRow(row);
    const available = subNodalsForRow(row);
    setDelegateTarget(available[0]?.sub_nodal_emp_id || "");
  };

  const doDelegate = async () => {
    if (!delegateRow || !delegateTarget) return;
    setDelegating(true);
    const res = await delegateDispatch({
      dispatch_id: delegateRow.id,
      sub_nodal_emp_id: delegateTarget,
    });
    setDelegating(false);
    if (res.success) {
      toast.success("No-Dues clearance delegated to Sub-Nodal.");
      setDelegateRow(null);
      load();
    } else {
      toast.error(res.message || "Failed to delegate.");
    }
  };

  const doRevoke = async (row: NoDuesDispatchRow) => {
    if (
      !(await confirm(
        `Take back this ${row.department_name} clearance from ${row.delegated_to_name || row.delegated_to_emp_id}?`,
        { title: "Revoke Delegation", confirmLabel: "Revoke", tone: "danger" }
      ))
    )
      return;
    const res = await revokeDelegation(row.id);
    if (res.success) {
      toast.success("Delegation revoked.");
      load();
    } else {
      toast.error(res.message || "Failed to revoke delegation.");
    }
  };

  const rows = tab === "Pending" ? pending : processed;

  return (
    <div className="page-wrapper">
      {confirmDialog}
      <div className="content pb-5">
        {/* Breadcrumbs & Page Header */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
          <div>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-1 small">
                <li className="breadcrumb-item">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(all_routes.Esepration.hub);
                    }}
                    className="text-decoration-none text-muted"
                  >
                    E-Separation
                  </a>
                </li>
                <li className="breadcrumb-item active text-primary fw-medium" aria-current="page">
                  No-Dues Clearance
                </li>
              </ol>
            </nav>
            <h4 className="fw-bold text-dark mb-0">No-Dues Clearance</h4>
          </div>
          <div className="d-flex align-items-center gap-2">
            <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate(-1)}>
              <i className="ti ti-arrow-left me-1" />
              Exit
            </button>
          </div>
        </div>

        {/* Sub-header Banner */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-body py-3 px-4">
            <div className="d-flex align-items-center gap-3">
              <span
                className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 10,
                  background: "#FBE9E2",
                  color: "#993C1D",
                }}
              >
                <i className="ti ti-clipboard-list" style={{ fontSize: 22 }} />
              </span>
              <div>
                <div
                  style={{
                    fontSize: "17px",
                    fontWeight: 700,
                    color: "#344054",
                    lineHeight: "1.3",
                  }}
                >
                  Department Clearances
                </div>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: 400,
                    color: "#667085",
                    lineHeight: "1.4",
                    marginTop: "2px",
                  }}
                >
                  No-Dues requests dispatched to your department
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Pending / Processed tabs */}
        <div
          className="d-flex p-1 mb-4"
          style={{ background: "#eef2f6", borderRadius: 10, maxWidth: 400 }}
        >
          {[
            { key: "Pending", label: `Pending (${pending.length})` },
            { key: "Processed", label: `Processed (${processed.length})` },
          ].map((t) => (
            <button
              key={t.key}
              className="btn flex-fill fw-semibold py-2"
              style={{
                borderRadius: 8,
                background: tab === t.key ? "#fff" : "transparent",
                color: tab === t.key ? "#993C1D" : "#6c757d",
                boxShadow: tab === t.key ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                fontSize: 14,
              }}
              onClick={() => setTab(t.key as "Pending" | "Processed")}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" />
            <p className="text-muted mt-3 mb-0">Loading clearances...</p>
          </div>
        ) : rows.length === 0 ? (
          <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
            <div className="card-body p-4">
              <WarningBanner
                type={tab === "Pending" ? "success" : "info"}
                message={
                  tab === "Pending"
                    ? "No pending No-Dues clearances on your desk."
                    : "No processed clearances yet."
                }
              />
            </div>
          </div>
        ) : (
          <div className="row g-3 mb-4">
            {rows.map((row) => {
              const meta = statusMeta(row.status);
              const info = empInfoMap[row.request_id] || {};
              const empName =
                (row as any).emp_name ||
                (row as any).employee_name ||
                info.emp_name ||
                "";
              const empNo =
                (row as any).emp_no ||
                (row as any).employee_no ||
                info.emp_no ||
                "";
              const designation =
                (row as any).designation || info.designation || "";

              const canDelegate =
                tab === "Pending" &&
                padEmpNo(row.approver_emp_id) === myEmpId &&
                subNodalsForRow(row).length > 0;

              return (
                <div className="col-md-6 col-xl-4" key={row.id}>
                  <div
                    className="card border-0 shadow-sm h-100"
                    style={{ borderRadius: 14 }}
                  >
                    <div
                      className="card-body d-flex flex-column p-3"
                      style={{ cursor: "pointer" }}
                      onClick={() => openAction(row)}
                    >
                      {/* Department & Status */}
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="flex-grow-1 overflow-hidden">
                          <div className="fw-semibold text-truncate text-dark" style={{ fontSize: 15 }}>
                            {row.department_name}
                          </div>
                          <div className="text-muted" style={{ fontSize: 12 }}>
                            {row.request_id}
                            {row.dispatched_at
                              ? ` • ${String(row.dispatched_at).slice(0, 10)}`
                              : ""}
                          </div>
                        </div>
                        <span
                          className="badge"
                          style={{ background: meta.bg, color: meta.color, fontSize: 12 }}
                        >
                          {meta.label}
                        </span>
                      </div>

                      {/* Prominent Employee Details Box */}
                      <div className="p-2 px-3 rounded-3 bg-light border mb-3 mt-1">
                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-1">
                          <div
                            className="fw-bold text-dark text-truncate"
                            style={{ fontSize: 13.5 }}
                          >
                            {empName || "Employee Details"}
                          </div>
                          {empNo && (
                            <span
                              className="badge bg-secondary-subtle text-secondary border border-secondary-subtle rounded-pill"
                              style={{ fontSize: 11 }}
                            >
                              {empNo}
                            </span>
                          )}
                        </div>
                      </div>

                      {row.delegated_to_name && (
                        <div className="small text-muted mb-2">
                          <i className="ti ti-user-share me-1" />
                          Delegated to:{" "}
                          <span className="fw-medium text-dark">{row.delegated_to_name}</span>
                        </div>
                      )}

                      <div className="d-flex align-items-center justify-content-between mt-auto pt-2 border-top">
                        {row.recovery_amount ? (
                          <span className="small fw-semibold text-danger">
                            Dues: ₹
                            {Number(row.recovery_amount).toLocaleString("en-IN")}
                          </span>
                        ) : (
                          <span className="small text-muted">
                            {tab === "Pending"
                              ? "Tap to record clearance"
                              : "Tap to view"}
                          </span>
                        )}
                        <i className="ti ti-chevron-right text-muted" />
                      </div>
                    </div>

                    {canDelegate && (
                      <div className="px-3 pb-3">
                        <button
                          className="btn btn-sm btn-outline-secondary w-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDelegate(row);
                          }}
                        >
                          <i className="ti ti-arrow-forward-up me-1" />
                          Delegate to Sub-Nodal
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Sub-Nodal management — only for the caller's own departments */}
        {isNodalOwner && (
          <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
            <div className="card-header bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
              <div>
                <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                  <span
                    className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                    style={{ width: 34, height: 34, fontSize: 15 }}
                  >
                    02
                  </span>
                  My Sub-Nodals
                </h5>
                <div className="text-muted small mt-1 ms-4 ps-2">
                  Create sub-nodals and hand off individual employees' No-Dues clearances to them
                </div>
              </div>
            </div>
            <div className="card-body p-4">
              <div className="row g-3 align-items-end mb-4">
                {myDepartments.length > 1 && (
                  <div className="col-md-4">
                    <FieldLabel label="Department" />
                    <select
                      className="form-select"
                      value={newSubNodalDeptId}
                      onChange={(e) =>
                        setNewSubNodalDeptId(
                          e.target.value ? Number(e.target.value) : ""
                        )
                      }
                    >
                      {myDepartments.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="col-md-5">
                  <FieldLabel label="Sub-Nodal Employee ID" />
                  <input
                    className="form-control"
                    placeholder="8-digit employee number"
                    value={newSubNodalEmpId}
                    onChange={(e) => setNewSubNodalEmpId(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <button
                    className="btn btn-primary w-100"
                    style={{ height: 42 }}
                    onClick={addSubNodal}
                    disabled={savingSubNodal}
                  >
                    {savingSubNodal ? (
                      <span className="spinner-border spinner-border-sm me-2" />
                    ) : (
                      <i className="ti ti-plus me-1" />
                    )}
                    Add Sub-Nodal
                  </button>
                </div>
              </div>

              {subNodals.length === 0 ? (
                <div className="text-center py-4 text-muted small">
                  <i className="ti ti-users fs-2 d-block mb-2 text-secondary" />
                  No sub-nodals registered yet. Add team members above to delegate clearances.
                </div>
              ) : (
                <div className="row g-3">
                  {subNodals.map((s) => (
                    <div key={s.id} className="col-md-6 col-lg-4">
                      <div className="p-3 rounded-3 border bg-light d-flex justify-content-between align-items-center">
                        <div>
                          <div className="fw-semibold text-dark">
                            {s.sub_nodal_name || s.sub_nodal_emp_id}
                          </div>
                          <small className="text-muted d-block">
                            {s.sub_nodal_emp_id} • {s.department_name}
                          </small>
                        </div>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          title="Remove"
                          onClick={() => removeSubNodal(s)}
                        >
                          <i className="ti ti-trash" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Delegated Clearances (in-flight handoffs) */}
              {delegated.length > 0 && (
                <div className="mt-4 pt-3 border-top">
                  <h6 className="fw-bold text-dark mb-3">
                    Currently Delegated Clearances ({delegated.length})
                  </h6>
                  <div className="d-flex flex-column gap-2">
                    {delegated.map((row) => (
                      <div
                        key={row.id}
                        className="d-flex justify-content-between align-items-center p-3 rounded-3 border bg-light"
                      >
                        <div>
                          <span className="fw-semibold text-dark">{row.request_id}</span>
                          <span className="text-muted small ms-2">
                            ({row.department_name}) →{" "}
                            <span className="fw-medium text-dark">
                              {row.delegated_to_name || row.delegated_to_emp_id}
                            </span>
                          </span>
                        </div>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => doRevoke(row)}
                        >
                          Revoke Delegation
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Delegate to Sub-Nodal Modal */}
      {delegateRow && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          style={{ background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: 14 }}>
              <div className="modal-header">
                <h5 className="modal-title fw-bold">Delegate Clearance</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setDelegateRow(null)}
                />
              </div>
              <div className="modal-body">
                <p className="text-muted small mb-3">
                  Hand off <strong>{delegateRow.department_name}</strong> clearance for request{" "}
                  <strong>{delegateRow.request_id}</strong> to one of your registered sub-nodals.
                </p>
                <FieldLabel label="Select Sub-Nodal" />
                <select
                  className="form-select"
                  value={delegateTarget}
                  onChange={(e) => setDelegateTarget(e.target.value)}
                >
                  {subNodalsForRow(delegateRow).map((s) => (
                    <option key={s.id} value={s.sub_nodal_emp_id}>
                      {s.sub_nodal_name || s.sub_nodal_emp_id} ({s.sub_nodal_emp_id})
                    </option>
                  ))}
                </select>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setDelegateRow(null)}
                  disabled={delegating}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={doDelegate}
                  disabled={delegating || !delegateTarget}
                >
                  {delegating ? (
                    <span className="spinner-border spinner-border-sm me-1" />
                  ) : (
                    <i className="ti ti-check me-1" />
                  )}
                  Confirm Delegate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NoDuesDeptClearance;
